import L from "leaflet";
import { createMapView, applyRegionMapConfig, setDataAttribution } from "./ui/MapView";
import { RadarLayer } from "./ui/RadarLayer";
import { StatusChip } from "./ui/StatusChip";
import { PlaybackBar } from "./ui/PlaybackBar";
import { LegendBar } from "./ui/LegendBar";
import { LoadingCard } from "./ui/LoadingCard";
import { ErrorBanner } from "./ui/ErrorBanner";
import { Toast } from "./ui/Toast";
import { createLocateButton } from "./ui/LocateButton";
import { RegionSwitch } from "./ui/RegionSwitch";
import { createOpacitySlider, createOpacityToggle } from "./ui/OpacityControl";
import {
  selectFromAdvertised,
  missingAdvertised,
  medianOf,
  classifyFreshness,
  hasEnoughFrames,
  selectInitialFrames,
  rotateFrames,
  pollCandidateTimes,
  INITIAL_PLAYABLE_COUNT,
  LOAD_BATCH_SIZE,
  POLL_MAX_NEW,
  type InitCandidate
} from "./lib/frames";
import { loadFrame, RadarApiError, fetchTimeExtent } from "./lib/radarApi";
import { formatUtcTooltip, type TimezoneMode } from "./lib/formatting";
import {
  SOURCES,
  sourcesForRegion,
  resolveStoredRegion,
  REGION_STORAGE_KEY,
  type RadarSource,
  type RegionId,
  type SourceId
} from "./lib/sources";

const POLL_INTERVAL_MS = 60_000;
const TICK_INTERVAL_MS = 15_000;
const PLAY_FRAME_MS = 500;
const PLAY_PAUSE_NEWEST_MS = 1500;
const OPACITY_STORAGE_KEY = "radar-opacity";
const DEFAULT_OPACITY = 0.7;
const MAX_POLL_FAILURES = 3;
/** Persistence keys per DESIGN.md Aneks M002 §A4. */
const TIMEZONE_STORAGE_KEY = "radar.timezone";
const SPEED_STORAGE_KEY = "radar.speed";

/** Public handle returned by bootstrapApp(); phase 2's RegionSwitch control wires its click handler to `switchRegion` (DESIGN.md Aneks M003 §R1-R2). Nothing in phase 1 calls it — the ONLY way to reach eu-de today is a pre-set `localStorage["radar.region"]`. */
export interface RadarAppHandle {
  switchRegion(next: RegionId): Promise<void>;
  getActiveRegion(): RegionId;
}

/** Per-region data state (M003 MULTI_REGION_RADAR.md): frames, verification stats and in-flight bookkeeping are isolated per region so a failure/poll in one never touches the other, and switching regions can resume an already-loaded region instantly from memory. */
interface RegionState {
  region: RegionId;
  /** Source actually in use within the region (primary, or the fallback once chosen at init — D-005/D-006). */
  sourceId: SourceId;
  activeFrames: number[];
  currentIndex: number;
  frameStats: Map<number, { px: number; bytes: number }>;
  retriedTimes: Set<number>;
  consecutivePollFailures: number;
  dataReady: boolean;
  pollInFlight: boolean;
  initLoading: boolean;
  abortController: AbortController | null;
}

function createRegionState(region: RegionId): RegionState {
  return {
    region,
    sourceId: sourcesForRegion(region)[0].id,
    activeFrames: [],
    currentIndex: 0,
    frameStats: new Map(),
    retriedTimes: new Set(),
    consecutivePollFailures: 0,
    dataReady: false,
    pollInFlight: false,
    initLoading: false,
    abortController: null
  };
}

function readStoredRegion(): string | null {
  try {
    return localStorage.getItem(REGION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function loadStoredOpacity(): number {
  try {
    const raw = localStorage.getItem(OPACITY_STORAGE_KEY);
    if (!raw) return DEFAULT_OPACITY;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0.3 || value > 1) return DEFAULT_OPACITY;
    return value;
  } catch {
    return DEFAULT_OPACITY;
  }
}

function loadStoredTimezoneMode(): TimezoneMode {
  try {
    return localStorage.getItem(TIMEZONE_STORAGE_KEY) === "utc" ? "utc" : "local";
  } catch {
    return "local";
  }
}

function loadStoredSpeed(): 1 | 2 {
  try {
    return localStorage.getItem(SPEED_STORAGE_KEY) === "2" ? 2 : 1;
  } catch {
    return 1;
  }
}

function mapAriaLabel(source: RadarSource): string {
  return `Mapa radaru opadów — ${source.displayName}`;
}

/** Wires up the full radar app inside `root`. Kept as one orchestrator per DECISIONS.md D-002 (no framework, no shared-state library needed at this size). */
export function bootstrapApp(root: HTMLElement): RadarAppHandle {
  root.innerHTML = "";

  // ---- Region state (M003) ----
  let activeRegion: RegionId = resolveStoredRegion(readStoredRegion());
  const regionStates: Record<RegionId, RegionState> = {
    us: createRegionState("us"),
    "eu-de": createRegionState("eu-de")
  };
  let state = regionStates[activeRegion];

  function activeSource(): RadarSource {
    return SOURCES[state.sourceId];
  }

  const initialSource = activeSource();

  const mapEl = document.createElement("div");
  mapEl.className = "map";
  mapEl.setAttribute("aria-label", mapAriaLabel(initialSource));
  root.appendChild(mapEl);

  const { map, attribution: mapAttribution, setBasemapRegion } = createMapView(
    mapEl,
    {
      region: initialSource.region,
      fitBounds: initialSource.fitBounds,
      minZoom: initialSource.minZoom,
      maxZoom: initialSource.maxZoom
    },
    initialSource.attribution
  );
  let currentAttributionText = initialSource.attribution;

  const radarLayer = new RadarLayer(map, loadStoredOpacity(), initialSource.overlayBounds);

  const statusChip = new StatusChip(initialSource.displayName);
  root.appendChild(statusChip.element);

  // DESIGN.md Aneks M003 §R1: positioned directly under the status chip.
  // switchRegion() is a hoisted function declaration further below in this
  // same closure — safe to reference here.
  const regionSwitch = new RegionSwitch({
    initialRegion: activeRegion,
    onSelect: (region: RegionId) => void switchRegion(region)
  });
  root.appendChild(regionSwitch.element);

  const loadingCard = new LoadingCard();
  root.appendChild(loadingCard.element);

  const errorBanner = new ErrorBanner(() => void init());
  root.appendChild(errorBanner.element);

  const toast = new Toast();
  root.appendChild(toast.element);

  // ---- Map controls column: Locate + Opacity toggle (native Leaflet zoom sits below, see styles.css) ----
  const controlsCol = document.createElement("div");
  controlsCol.className = "map-controls";
  root.appendChild(controlsCol);

  let locationMarker: L.CircleMarker | null = null;

  const locateBtn = createLocateButton(() => {
    if (!("geolocation" in navigator)) {
      toast.show("Geolokalizacja nie jest dostępna w tej przeglądarce.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const src = activeSource();
        if (locationMarker) {
          locationMarker.setLatLng([latitude, longitude]);
        } else {
          locationMarker = L.circleMarker([latitude, longitude], {
            radius: 7,
            color: "#2563eb",
            weight: 2,
            fillColor: "#2563eb",
            fillOpacity: 0.8
          }).addTo(map);
        }
        const withinCoverage =
          longitude >= src.fitBounds.west &&
          longitude <= src.fitBounds.east &&
          latitude >= src.fitBounds.south &&
          latitude <= src.fitBounds.north;
        if (withinCoverage) {
          map.flyTo([latitude, longitude], Math.max(src.minZoom, 7));
          return;
        }
        if (src.region === "eu-de") {
          // DESIGN.md Aneks M003 §R3 "Geolokalizacja": outside DWD's coverage
          // centers back on the region's bounds rather than a location with no data.
          const bounds = L.latLngBounds(
            [src.fitBounds.south, src.fitBounds.west],
            [src.fitBounds.north, src.fitBounds.east]
          );
          map.flyToBounds(bounds, { padding: [8, 8] });
          toast.show("Radar obejmuje obecnie Niemcy (DWD).");
        } else {
          toast.show("Radar obejmuje kontynentalne USA.");
        }
      },
      () => {
        toast.show("Nie udało się ustalić lokalizacji.");
      },
      { timeout: 8000 }
    );
  });
  controlsCol.appendChild(locateBtn);

  function applyOpacity(value: number): void {
    radarLayer.setOpacity(value);
    try {
      localStorage.setItem(OPACITY_STORAGE_KEY, String(value));
    } catch {
      /* ignore storage errors (private mode / quota) */
    }
    desktopOpacitySlider.setValue(value);
    mobileOpacitySlider.setValue(value);
  }

  const initialOpacity = loadStoredOpacity();
  const desktopOpacitySlider = createOpacitySlider(initialOpacity, applyOpacity);
  const mobileOpacitySlider = createOpacitySlider(initialOpacity, applyOpacity);

  const opacityPopover = document.createElement("div");
  opacityPopover.className = "opacity-popover card";
  opacityPopover.hidden = true;
  opacityPopover.appendChild(desktopOpacitySlider.element);

  const opacityToggle = createOpacityToggle(opacityPopover);
  controlsCol.append(opacityToggle, opacityPopover);

  document.addEventListener("click", (e) => {
    if (
      !opacityPopover.hidden &&
      !opacityPopover.contains(e.target as Node) &&
      e.target !== opacityToggle
    ) {
      opacityPopover.hidden = true;
      opacityToggle.setAttribute("aria-expanded", "false");
    }
  });

  const legend = new LegendBar({ opacitySliderElement: mobileOpacitySlider.element });
  root.appendChild(legend.element);
  legend.setSource(initialSource.legend); // paints the active source's palette (M003 phase 2, see LegendBar.ts)

  // ---- Playback state (shared UI prefs — not per-region) ----
  let playing = false;
  let playTimer: number | undefined;
  let pollTimer: number | undefined;
  let tickTimer: number | undefined;
  /** Aneks M002 §A4: persisted playback speed and timezone display mode. */
  let speed: 1 | 2 = loadStoredSpeed();
  let timezoneMode: TimezoneMode = loadStoredTimezoneMode();

  function handleToggleSpeed(): void {
    speed = speed === 2 ? 1 : 2;
    try {
      localStorage.setItem(SPEED_STORAGE_KEY, String(speed));
    } catch {
      /* ignore storage errors (private mode / quota) */
    }
    playbackBar.setSpeed(speed);
  }

  function handleToggleTimezone(): void {
    timezoneMode = timezoneMode === "utc" ? "local" : "utc";
    try {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, timezoneMode);
    } catch {
      /* ignore storage errors (private mode / quota) */
    }
    playbackBar.setTimezoneMode(timezoneMode);
    statusChip.setTimezoneMode(timezoneMode);
  }

  const playbackBar = new PlaybackBar({
    onTogglePlay: () => togglePlay(),
    onScrub: (index) => {
      stopPlaying();
      setIndex(index);
    },
    onToggleSpeed: () => handleToggleSpeed(),
    onToggleTimezone: () => handleToggleTimezone()
  });
  root.appendChild(playbackBar.element);
  playbackBar.setSpeed(speed);
  playbackBar.setTimezoneMode(timezoneMode);
  statusChip.setTimezoneMode(timezoneMode);

  function setIndex(index: number): void {
    if (state.activeFrames.length === 0) return;
    state.currentIndex = Math.max(0, Math.min(index, state.activeFrames.length - 1));
    playbackBar.setIndex(state.currentIndex);
    radarLayer.showFrame(state.activeFrames[state.currentIndex]);
  }

  function stepFrame(delta: number): void {
    stopPlaying();
    setIndex(state.currentIndex + delta);
  }

  function stopPlaying(): void {
    playing = false;
    playbackBar.setPlaying(false);
    if (playTimer !== undefined) {
      window.clearTimeout(playTimer);
      playTimer = undefined;
    }
  }

  function togglePlay(): void {
    if (!state.dataReady) return;
    if (playing) {
      stopPlaying();
      return;
    }
    playing = true;
    playbackBar.setPlaying(true);
    scheduleNextFrame();
  }

  function scheduleNextFrame(): void {
    if (!playing) return;
    const atNewest = state.currentIndex >= state.activeFrames.length - 1;
    const frameMs = speed === 2 ? PLAY_FRAME_MS / 2 : PLAY_FRAME_MS;
    const delay = atNewest ? PLAY_PAUSE_NEWEST_MS : frameMs;
    playTimer = window.setTimeout(() => {
      if (!playing) return;
      const nextIndex = atNewest ? 0 : state.currentIndex + 1;
      setIndex(nextIndex);
      scheduleNextFrame();
    }, delay);
  }

  function updateStatusChipNow(): void {
    if (state.activeFrames.length === 0) return;
    const latest = state.activeFrames[state.activeFrames.length - 1];
    const src = activeSource();
    let freshness = classifyFreshness(latest, Date.now(), src.freshness.freshMaxMs, src.freshness.agingMaxMs);
    if (state.consecutivePollFailures >= MAX_POLL_FAILURES) {
      freshness = "stale";
    }
    statusChip.update(latest, freshness, Date.now());

    if (freshness === "stale") {
      errorBanner.show(
        `Dane radaru mogą być nieaktualne. Ostatnia obserwacja: ${formatUtcTooltip(latest)}.`
      );
    } else {
      errorBanner.hide();
    }
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (!state.dataReady) return;
    const target = e.target as HTMLElement | null;
    if (
      target instanceof HTMLInputElement &&
      target.type === "range" &&
      target.getAttribute("aria-label") === "Krycie warstwy radaru"
    ) {
      return; // let the opacity slider handle its own arrow keys
    }
    // Space must keep activating whichever button/link has keyboard focus
    // (a11y); the global play/pause shortcut applies only outside them.
    // The play button itself toggles via its native click, so skipping it
    // here also avoids a double toggle.
    if (
      (e.key === " " || e.key === "Spacebar") &&
      target instanceof HTMLElement &&
      target.closest("button, a, input, select, textarea, [role='button']")
    ) {
      return;
    }
    switch (e.key) {
      case " ":
      case "Spacebar":
        e.preventDefault();
        togglePlay();
        break;
      case "ArrowLeft":
        e.preventDefault();
        stepFrame(-1);
        break;
      case "ArrowRight":
        e.preventDefault();
        stepFrame(1);
        break;
      case "Home":
        e.preventDefault();
        stopPlaying();
        setIndex(0);
        break;
      case "End":
        e.preventDefault();
        stopPlaying();
        setIndex(state.activeFrames.length - 1);
        break;
      default:
        break;
    }
  }
  document.addEventListener("keydown", handleKeydown);

  function finishReveal(): void {
    loadingCard.hide();
    playbackBar.show();
    playbackBar.setDisabled(false);
    state.dataReady = true;
    state.consecutivePollFailures = 0;
    updateStatusChipNow();

    if (pollTimer === undefined) {
      pollTimer = window.setInterval(() => pollTick(), POLL_INTERVAL_MS);
    }
    if (tickTimer === undefined) {
      tickTimer = window.setInterval(updateStatusChipNow, TICK_INTERVAL_MS);
    }
  }

  // ==== Advertised-list sources: NCEP WMS (D-005) and DWD (D-006) ====
  // Both a source's availableTimes() returns REAL advertised timestamps
  // (irregular ~2 min for WMS, regular ~5 min for DWD) rather than a
  // synthetic grid, so the window is progressively verified newest-first
  // exactly like DESIGN.md Aneks M002 §A7 describes — generalized here across
  // any such source instead of being WMS-specific.

  /**
   * Progressive, newest-first verification of `src`'s advertised window into
   * region state `st`. `st` is captured once at call time and used for every
   * data mutation so a region switch reassigning the ambient `state` binding
   * mid-load can never make this loop write into the WRONG region — the
   * function instead notices the abort (switchRegion always aborts the
   * region it is leaving, DESIGN.md Aneks M003 §R2 step 2) and stops without
   * touching shared UI, leaving whatever was already accepted in `st` intact
   * for an instant render if the user switches back before revoke() runs.
   */
  async function initAdvertisedSource(
    src: RadarSource,
    st: RegionState,
    advertisedMs: number[]
  ): Promise<void> {
    const targetCount = Math.max(1, Math.round(src.historyWindowMs / src.cadenceMs));
    const target = selectFromAdvertised(advertisedMs, targetCount);
    const newestFirst = [...target].reverse();
    const totalTarget = newestFirst.length;
    const now = Date.now();

    loadingCard.setMessage(`Wczytywanie danych radaru (${src.displayName})…`);
    loadingCard.setProgress(0, totalTarget);
    // Fix the ruler's domain to the full target window up front (§A7): ticks
    // must not shift as later batches fill in older frames.
    if (totalTarget > 0) {
      playbackBar.setDomain(target[0], target[target.length - 1]);
    }

    const acceptedTimes: number[] = [];
    let newerAccepted: { px: number; bytes: number } | undefined;
    let revealed = false;
    let doneCount = 0;
    st.initLoading = true;

    for (let i = 0; i < newestFirst.length; i += LOAD_BATCH_SIZE) {
      if (st.abortController?.signal.aborted) {
        st.initLoading = false;
        return;
      }
      const batch = newestFirst.slice(i, i + LOAD_BATCH_SIZE);
      const results = await Promise.all(
        batch.map(async (t) => {
          try {
            const url = src.buildFrameUrl(t, st.retriedTimes.has(t) ? Date.now() : undefined);
            const frame = await loadFrame(url, st.abortController?.signal ?? undefined);
            return { t, frame, failed: false as const };
          } catch {
            return { t, frame: null, failed: true as const };
          }
        })
      );

      if (st.abortController?.signal.aborted) {
        for (const r of results) {
          if (!r.failed && r.frame?.objectUrl) URL.revokeObjectURL(r.frame.objectUrl);
        }
        st.initLoading = false;
        return;
      }

      // results are already newest→oldest (batch was sliced from newestFirst)
      for (const r of results) {
        doneCount += 1;
        if (!revealed) {
          loadingCard.setProgress(doneCount, totalTarget);
        } else {
          // LoadingCard only covers the pre-reveal phase; once the timeline
          // is visible, the ruler's own micro-label communicates ongoing
          // background batches (§A7 "WCZYTYWANIE X/N").
          playbackBar.setLoadingProgress(doneCount, totalTarget, doneCount < totalTarget);
        }

        if (r.failed || r.frame === null) {
          st.retriedTimes.add(r.t);
          continue;
        }
        const median = medianOf([...st.frameStats.values()].map((s) => s.px));
        const verdict = src.validateFrame({
          pixelCount: r.frame.pixelCount,
          byteSize: r.frame.byteSize,
          ageMs: now - r.t,
          windowMedianPixels: median,
          prevPixelCount: newerAccepted?.px,
          prevByteSize: newerAccepted?.bytes
        });

        if (verdict !== "accept") {
          if (r.frame.objectUrl !== null) URL.revokeObjectURL(r.frame.objectUrl);
          if (newerAccepted === undefined) {
            // Still at the tip: nothing newer verified yet — retry on the next poll.
            st.retriedTimes.add(r.t);
          }
          // Else: older than an accepted frame — the gap stays, honestly (D-003/D-004).
          continue;
        }

        st.retriedTimes.delete(r.t);
        radarLayer.addFrame(r.t, r.frame.objectUrl as string);
        st.frameStats.set(r.t, { px: r.frame.pixelCount, bytes: r.frame.byteSize });
        acceptedTimes.push(r.t);
        newerAccepted = { px: r.frame.pixelCount, bytes: r.frame.byteSize };
      }

      // Capture the user's CURRENT selection before reassigning the window —
      // the user may have scrubbed between batches; snapping back to a value
      // captured a batch ago would revert their choice.
      const prevSelectedTime = st.activeFrames[st.currentIndex];
      st.activeFrames = acceptedTimes.slice().sort((a, b) => a - b);
      playbackBar.setFrames(st.activeFrames);

      if (!revealed) {
        const minWanted = Math.min(INITIAL_PLAYABLE_COUNT, totalTarget);
        if (st.activeFrames.length >= minWanted) {
          revealed = true;
          finishReveal();
          setIndex(st.activeFrames.length - 1);
        }
      } else {
        const idx = prevSelectedTime !== undefined ? st.activeFrames.indexOf(prevSelectedTime) : -1;
        setIndex(idx >= 0 ? idx : st.activeFrames.length - 1);
      }
    }
    st.initLoading = false;
    // All batches done: the micro-label disappears and the domain is free
    // to slide again with future polls (steady-state, like the ArcGIS source).
    playbackBar.setLoadingProgress(doneCount, totalTarget, false);
    playbackBar.releaseDomainLock();

    if (!hasEnoughFrames(st.activeFrames.length)) {
      loadingCard.hide();
      playbackBar.hide();
      playbackBar.setDisabled(true);
      st.dataReady = false;
      errorBanner.show(
        `Nie udało się wczytać wystarczającej liczby klatek radaru (${src.displayName}).`
      );
      return;
    }

    if (!revealed) {
      // Only possible when totalTarget itself is below INITIAL_PLAYABLE_COUNT
      // but hasEnoughFrames (MIN_OK_FRAMES) still passed.
      finishReveal();
      setIndex(st.activeFrames.length - 1);
    }
  }

  /**
   * Append one newly-verified frame to the growing advertised-source window.
   * Unlike the fixed-length `rotateFrames` (ArcGIS fallback), the window
   * grows up to the source's `historyWindowMs`/`cadenceMs` target before
   * anything is dropped (D-005: real advertised cadence, no synthetic grid).
   */
  function appendFrame(
    src: RadarSource,
    st: RegionState,
    timeMs: number,
    objectUrl: string,
    px: number,
    bytes: number
  ): void {
    const targetCount = Math.max(1, Math.round(src.historyWindowMs / src.cadenceMs));
    const wasAtNewest = st.currentIndex === st.activeFrames.length - 1;
    const currentFrameTime = st.activeFrames[st.currentIndex];
    const oldestTime = st.activeFrames[0];

    radarLayer.addFrame(timeMs, objectUrl);
    let next = [...st.activeFrames, timeMs].sort((a, b) => a - b);
    if (next.length > targetCount) {
      next = next.slice(next.length - targetCount);
    }
    if (!next.includes(oldestTime)) {
      radarLayer.removeFrame(oldestTime);
      st.frameStats.delete(oldestTime);
      st.retriedTimes.delete(oldestTime);
    }
    st.activeFrames = next;
    st.frameStats.set(timeMs, { px, bytes });
    playbackBar.setFrames(st.activeFrames);

    if (wasAtNewest) {
      st.currentIndex = st.activeFrames.length - 1;
    } else {
      const idx = st.activeFrames.indexOf(currentFrameTime);
      st.currentIndex = idx >= 0 ? idx : 0;
    }
    setIndex(st.currentIndex);
  }

  async function pollAdvertisedSource(src: RadarSource, st: RegionState): Promise<void> {
    if (!st.dataReady || st.pollInFlight || st.initLoading) return;
    st.pollInFlight = true;
    try {
      const advertised = await src.availableTimes(st.abortController?.signal ?? undefined);
      st.consecutivePollFailures = 0;
      const currentLatest = st.activeFrames[st.activeFrames.length - 1];
      const missing = missingAdvertised(advertised, currentLatest, POLL_MAX_NEW);
      for (const t of missing) {
        try {
          statusChip.setUpdating(true);
          const url = src.buildFrameUrl(t, st.retriedTimes.has(t) ? Date.now() : undefined);
          const frame = await loadFrame(url, st.abortController?.signal ?? undefined);
          const newest = st.activeFrames[st.activeFrames.length - 1];
          const prev = st.frameStats.get(newest);
          const median = medianOf([...st.frameStats.values()].map((s) => s.px));
          const verdict = src.validateFrame({
            pixelCount: frame.pixelCount,
            byteSize: frame.byteSize,
            ageMs: Date.now() - t,
            windowMedianPixels: median,
            prevPixelCount: prev?.px,
            prevByteSize: prev?.bytes
          });
          statusChip.setUpdating(false);
          if (verdict === "accept" && frame.objectUrl !== null) {
            appendFrame(src, st, t, frame.objectUrl, frame.pixelCount, frame.byteSize);
            st.retriedTimes.delete(t);
          } else {
            // Not rendered yet (or duplicate/undecodable): keep the last good
            // frame, remember to cache-bust this slot, retry on a later poll.
            if (frame.objectUrl !== null) URL.revokeObjectURL(frame.objectUrl);
            st.retriedTimes.add(t);
            break;
          }
        } catch {
          st.retriedTimes.add(t);
          statusChip.setUpdating(false);
          break;
        }
      }
      updateStatusChipNow();
    } catch {
      st.consecutivePollFailures += 1;
      updateStatusChipNow();
    } finally {
      st.pollInFlight = false;
    }
  }

  // ==== ArcGIS fallback (D-001/D-003/D-004) — unchanged flow, region-scoped ====

  async function preloadFrames(src: RadarSource, st: RegionState, times: number[]): Promise<number[]> {
    const candidates: InitCandidate[] = [];
    const objectUrls = new Map<number, string>();
    let done = 0;
    loadingCard.setProgress(0, times.length);
    await Promise.all(
      times.map(async (t) => {
        try {
          const frame = await loadFrame(src.buildFrameUrl(t), st.abortController?.signal ?? undefined);
          candidates.push({ timeMs: t, pixelCount: frame.pixelCount, byteSize: frame.byteSize });
          if (frame.objectUrl !== null) {
            objectUrls.set(t, frame.objectUrl);
          }
        } catch {
          // Skipped per DESIGN.md §9 "Błąd pojedynczej klatki" — handled below via hasEnoughFrames.
        } finally {
          done += 1;
          loadingCard.setProgress(done, times.length);
        }
      })
    );
    if (st.abortController?.signal.aborted) {
      for (const url of objectUrls.values()) URL.revokeObjectURL(url);
      return [];
    }
    candidates.sort((a, b) => a.timeMs - b.timeMs);
    // Only fully fetched, decoded and validated frames may enter the timeline;
    // trailing not-yet-rendered candidates are dropped (see selectInitialFrames).
    const selected = selectInitialFrames(candidates, Date.now()).filter((t) =>
      objectUrls.has(t)
    );
    const selectedSet = new Set(selected);
    st.frameStats.clear();
    st.retriedTimes.clear();
    for (const c of candidates) {
      if (selectedSet.has(c.timeMs)) {
        st.frameStats.set(c.timeMs, { px: c.pixelCount, bytes: c.byteSize });
      }
    }
    for (const [t, url] of objectUrls) {
      if (selectedSet.has(t)) {
        radarLayer.addFrame(t, url);
      } else {
        URL.revokeObjectURL(url);
      }
    }
    return selected;
  }

  async function initArcgisFlow(src: RadarSource, st: RegionState, requestedTimes: number[]): Promise<void> {
    const loaded = await preloadFrames(src, st, requestedTimes);
    if (st.abortController?.signal.aborted) return;

    if (!hasEnoughFrames(loaded.length)) {
      loadingCard.hide();
      errorBanner.show(
        `Nie udało się wczytać wystarczającej liczby klatek radaru z serwisu NOAA/NWS (${src.displayName}).`
      );
      st.dataReady = false;
      return;
    }

    st.activeFrames = loaded;
    playbackBar.setFrames(st.activeFrames);
    st.currentIndex = st.activeFrames.length - 1;
    setIndex(st.currentIndex);

    finishReveal();
  }

  /** Append one validated frame to the timeline, rotating the fixed-length window (ArcGIS fallback). */
  function acceptFrame(
    st: RegionState,
    timeMs: number,
    objectUrl: string,
    px: number,
    bytes: number
  ): void {
    const wasAtNewest = st.currentIndex === st.activeFrames.length - 1;
    const currentFrameTime = st.activeFrames[st.currentIndex];
    const oldestTime = st.activeFrames[0];

    radarLayer.addFrame(timeMs, objectUrl);
    const rotated = rotateFrames(st.activeFrames, timeMs);
    if (rotated.length <= st.activeFrames.length && !rotated.includes(oldestTime)) {
      radarLayer.removeFrame(oldestTime);
      st.frameStats.delete(oldestTime);
      st.retriedTimes.delete(oldestTime);
    }
    st.activeFrames = rotated;
    st.frameStats.set(timeMs, { px, bytes });
    playbackBar.setFrames(st.activeFrames);

    if (wasAtNewest) {
      st.currentIndex = st.activeFrames.length - 1;
    } else {
      const idx = st.activeFrames.indexOf(currentFrameTime);
      st.currentIndex = idx >= 0 ? idx : 0;
    }
    setIndex(st.currentIndex);
  }

  async function pollArcgisFlow(src: RadarSource, st: RegionState): Promise<void> {
    if (!st.dataReady || st.pollInFlight) return;
    st.pollInFlight = true;
    try {
      const extent = await fetchTimeExtent(st.abortController?.signal ?? undefined);
      st.consecutivePollFailures = 0;
      const currentLatest = st.activeFrames[st.activeFrames.length - 1];
      // Every advertised slot newer than our newest accepted frame, oldest
      // first, so a slot rejected earlier is re-checked and added the moment
      // the server has actually rendered it.
      const slots = pollCandidateTimes(currentLatest, extent.endMs);
      for (const slot of slots) {
        try {
          statusChip.setUpdating(true);
          const url = src.buildFrameUrl(slot, st.retriedTimes.has(slot) ? Date.now() : undefined);
          const frame = await loadFrame(url, st.abortController?.signal ?? undefined);
          const newest = st.activeFrames[st.activeFrames.length - 1];
          const prev = st.frameStats.get(newest);
          const verdict = src.validateFrame({
            pixelCount: frame.pixelCount,
            byteSize: frame.byteSize,
            ageMs: Date.now() - slot,
            windowMedianPixels: medianOf([...st.frameStats.values()].map((s) => s.px)),
            prevPixelCount: prev?.px,
            prevByteSize: prev?.bytes
          });
          statusChip.setUpdating(false);
          if (verdict === "accept" && frame.objectUrl !== null) {
            acceptFrame(st, slot, frame.objectUrl, frame.pixelCount, frame.byteSize);
            st.retriedTimes.delete(slot);
          } else {
            // Not rendered yet (or not an image): keep the last good frame,
            // remember to cache-bust this slot, retry on a later poll. Stop
            // here so the timeline never gets holes between accepted frames.
            if (frame.objectUrl !== null) URL.revokeObjectURL(frame.objectUrl);
            st.retriedTimes.add(slot);
            break;
          }
        } catch {
          // Network/HTTP failure for this slot; retry on the next poll.
          statusChip.setUpdating(false);
          st.retriedTimes.add(slot);
          break;
        }
      }
      updateStatusChipNow();
    } catch {
      st.consecutivePollFailures += 1;
      updateStatusChipNow();
    } finally {
      st.pollInFlight = false;
    }
  }

  // ==== Region-aware bootstrap / poll dispatch (M003) ====

  /**
   * Load one region's data from scratch: try each of its sources in order
   * (US: NCEP WMS primary → ArcGIS fallback; EU: DWD only, D-006) until one
   * successfully lists times, then delegate to the matching load flow. Only
   * ADVANCES to the next candidate when a source fails to even list times —
   * once a source's list succeeds, that source is committed to for the rest
   * of this load (unchanged pre-M003 behaviour: a WMS load that comes up
   * short on verified frames shows its own error rather than silently
   * retrying via ArcGIS).
   */
  async function initRegion(region: RegionId): Promise<void> {
    const st = regionStates[region];
    st.dataReady = false;
    st.initLoading = false;
    st.pollInFlight = false;
    st.frameStats.clear();
    st.retriedTimes.clear();
    st.activeFrames = [];
    st.currentIndex = 0;
    st.consecutivePollFailures = 0;
    st.abortController = new AbortController();

    const candidates = sourcesForRegion(region);
    for (let i = 0; i < candidates.length; i++) {
      const src = candidates[i];
      const isLast = i === candidates.length - 1;
      let advertised: number[] | null = null;
      let availErr: unknown = null;
      try {
        advertised = await src.availableTimes(st.abortController.signal);
      } catch (err) {
        advertised = null;
        availErr = err;
      }
      if (st.abortController.signal.aborted) return;

      if (advertised !== null) {
        st.sourceId = src.id;
        if (src.id === "us-arcgis") {
          await initArcgisFlow(src, st, advertised);
        } else {
          await initAdvertisedSource(src, st, advertised);
        }
        return;
      }

      if (isLast) {
        loadingCard.hide();
        const detail = availErr instanceof RadarApiError ? ` (${availErr.message})` : "";
        errorBanner.show(`Nie udało się pobrać danych radaru (${src.displayName}).${detail}`);
        st.dataReady = false;
      }
    }
  }

  /** Cold-boot / retry: (re)loads ONLY the currently active region, clearing just its own overlays first (never the other region's cached ones). */
  async function init(): Promise<void> {
    stopPlaying();
    radarLayer.revoke(state.activeFrames);
    playbackBar.hide();
    playbackBar.setDisabled(true);
    errorBanner.hide();
    statusChip.setLoading();
    loadingCard.show();
    loadingCard.setMessage(`Łączenie z serwisem ${activeSource().displayName}…`);

    await initRegion(activeRegion);
  }

  function pollTick(): void {
    // Always the CURRENTLY ACTIVE region's source — the inactive region has
    // no timer of its own, so it makes zero requests while backgrounded
    // (MULTI_REGION_RADAR.md "Lazy").
    const src = SOURCES[state.sourceId];
    if (src.id === "us-arcgis") {
      void pollArcgisFlow(src, state);
    } else {
      void pollAdvertisedSource(src, state);
    }
  }

  /**
   * Region switch (DESIGN.md Aneks M003 §R2, steps 2–7). Not wired to any
   * control in phase 1 (RegionSwitch is phase-2 UI) — exposed on the
   * returned RadarAppHandle so phase 2 only has to add the control and call
   * this.
   */
  async function switchRegion(next: RegionId): Promise<void> {
    if (next === activeRegion) return;
    try {
      localStorage.setItem(REGION_STORAGE_KEY, next);
    } catch {
      /* ignore storage errors (private mode / quota) */
    }

    const leavingRegion = activeRegion;
    const leaving = regionStates[leavingRegion];

    // Step 2: pause; disable time controls; abort the leaving region's
    // in-flight work; its overlay is hidden (not revoked — kept in memory in
    // case the user switches straight back, §R2.7) and its banner clears.
    stopPlaying();
    playbackBar.setDisabled(true);
    errorBanner.hide();
    leaving.abortController?.abort();
    leaving.pollInFlight = false;
    leaving.initLoading = false;
    radarLayer.hideCurrent();

    activeRegion = next;
    state = regionStates[next];
    const primary = sourcesForRegion(next)[0];

    // Step 3: atomic identity swap BEFORE the new region's first frame —
    // chip, legend, attribution, aria-label, map bounds/zoom all flip together.
    statusChip.setDisplayName(primary.displayName);
    statusChip.setLoading();
    legend.setSource(primary.legend);
    setDataAttribution(mapAttribution, currentAttributionText, primary.attribution);
    currentAttributionText = primary.attribution;
    mapEl.setAttribute("aria-label", mapAriaLabel(primary));
    radarLayer.setBounds(primary.overlayBounds);
    setBasemapRegion(primary.region); // D-017: USGS dla USA, NASA GIBS poza USA
    applyRegionMapConfig(
      map,
      { region: primary.region, fitBounds: primary.fitBounds, minZoom: primary.minZoom, maxZoom: primary.maxZoom },
      { animate: true }
    );
    playbackBar.hide();
    playbackBar.setFrames([]);
    loadingCard.show();
    loadingCard.setMessage(`Łączenie z serwisem ${primary.displayName}…`);

    // Steps 4-5: load the entering region (newest-first reveal, background batches).
    await initRegion(next);

    // Re-entrancy guard (reviewer HIGH-1): if the user switched AGAIN while
    // initRegion was awaiting, `leaving` may now BE the active region — its
    // blobs must not be revoked and its state must not be zeroed, or the
    // active map would go blank with dead controls. The newest switch call's
    // own tail owns the cleanup instead.
    if (activeRegion !== next) return;

    // Step 6: only now free the LEAVING region's blobs — it was kept alive
    // in memory until the new region was ready/attempted, so switching back
    // during that window would have rendered instantly (§R2.7).
    radarLayer.revoke(leaving.activeFrames);
    leaving.activeFrames = [];
    leaving.frameStats.clear();
    leaving.retriedTimes.clear();
    leaving.dataReady = false;
  }

  void init();

  return {
    switchRegion,
    getActiveRegion: () => activeRegion
  };
}
